'use client';

import { WebshopProduct } from '@cldsystems/ws-api-client';
import { FC, useEffect } from 'react';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useStore } from '@/store/zustand/zustand.setup';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import filterInactivatedProducts from '@/utils/filterInactivatedProducts';

type MaybeProductsContext = {
  allProducts?: { items: WebshopProduct[]; total: number };
};

/**
 * Runs the post-deploy reconciliation of the persisted local cart / favorites
 * EXACTLY ONCE per render-tree, instead of from inside `useMyCart` (which is
 * called from every `ProductCard`, `LanguageSelector`, etc.).
 *
 * Why a dedicated component?
 *   The previous implementation lived in `useMyCart` / `useFavorites`. Those
 *   hooks are called from dozens of components per page (every product card,
 *   the language selector, the my-cart button, the upsell dialog, …). Putting
 *   a Zustand-write effect on that hot path made the write race with another
 *   component's render the moment the auth flag flipped, producing the
 *   React 19 diagnostic:
 *     "Cannot update a component (`LanguageSelector`) while rendering a
 *      different component (`ProductCard`)".
 *
 *   By isolating the write into a single mount point at the top of the tree
 *   (just under `AppProvider`, which supplies the `allProducts` context this
 *   needs), the reconciliation runs once per deploy and never fans out.
 *
 * Why `useStore.getState()` and not the hook?
 *   We don't WANT to subscribe — subscribing to `general.deploymentVersion`
 *   would cause this component to re-render every time we stamp the new
 *   version, which is wasteful (the component renders nothing). Reading the
 *   value imperatively inside the effect is sufficient because the effect
 *   only needs the value at the moment it runs.
 */
const DeploymentVersionReconciler: FC = () => {
  // We read the context defensively (instead of `useAllProductsContext`),
  // because some routes mounted via `<Page>` don't pass an `allProducts`
  // context payload (e.g. `/access-denied`, `/order-status`). Throwing
  // there would break SSR for those routes; just bailing gracefully keeps
  // the reconciler "best-effort" — when the catalog isn't available we
  // simply stamp the deploy version (still unblocks favorites cleanup
  // logic) without filtering, and skip the cart/favorites pruning step.
  const ctx = useAppContext<MaybeProductsContext | undefined>();
  const allProductsItems: WebshopProduct[] | undefined = ctx?.allProducts?.items;

  const currentVersion = process.env.NEXT_PUBLIC_DEPLOYMENT_VERSION;
  useEffect(() => {
    const {
      general: { deploymentVersion },
      setDeploymentVersion,
      setMyCart,
      setFavourites,
      clearOptedOut,
      myCart,
      favorites,
    } = useStore.getState();

    if (!deploymentVersion) {
      // First mount with no persisted version: just stamp it and bail.
      // There is nothing in the persisted slices to reconcile against the
      // current product catalog yet.
      setDeploymentVersion(currentVersion);
      return;
    }

    if (currentVersion === deploymentVersion) return;

    // No catalog available on this route — stamp the version and bail.
    // The next page navigation that DOES carry `allProducts` (typically the
    // landing page or any /products/* route) will run the actual prune.
    if (!allProductsItems) {
      setDeploymentVersion(currentVersion);
      return;
    }

    clearOptedOut();

    const activeCartProducts = filterInactivatedProducts(
      allProductsItems,
      myCart.products as WithQuantity<SimpleProductInfo>[],
    ) as WithQuantity<SimpleProductInfo>[];

    const amount = activeCartProducts.reduce((acc, current) => acc + current.price, 0);
    const discountedAmount = activeCartProducts.reduce(
      (acc, current) => acc + current.discountedPrice,
      0,
    );
    const totalAmount = activeCartProducts.reduce(
      (acc, current) => acc + current.discountedPrice,
      0,
    );

    setMyCart({
      products: activeCartProducts,
      amount,
      discountedAmount,
      totalAmount,
    });

    const activeFavoriteProducts = filterInactivatedProducts(allProductsItems, favorites.products);

    setFavourites({ products: activeFavoriteProducts });

    // Stamp the new deploy version LAST so any observer sees a fully
    // reconciled cart + favorites before the version change.
    setDeploymentVersion(currentVersion);
    // `allProductsItems` is the only meaningful dep — re-running on every
    // store change would invert the whole optimisation. The effect is itself
    // idempotent once `deploymentVersion === currentVersion`.
  }, [allProductsItems, currentVersion]);

  return null;
};

export default DeploymentVersionReconciler;
